<template>
  <div class="blog-post-page">
    <main class="blog-post-main">
      <div v-if="loading" class="blog-post-state">
        <v-progress-circular indeterminate color="#ffcf5a" />
        <p>Carregando matéria…</p>
      </div>

      <div v-else-if="error" class="blog-post-state blog-post-state--error" role="alert">
        <v-icon large color="#f25568">mdi-file-search-outline</v-icon>
        <h1 class="blog-post-state__title">Matéria não encontrada</h1>
        <p>Esta matéria não existe ou foi removida.</p>
        <router-link class="zinnes-primary-btn blog-post-state__action" :to="{ name: 'BlogHome' }">
          Voltar ao Portal Zinnes
        </router-link>
      </div>

      <article v-else class="blog-post" aria-labelledby="blog-post-title">
        <header class="blog-post__header">
          <nav class="blog-post__back" aria-label="Voltar ao portal zinnes">
            <router-link :to="backLink">
              <v-icon small>mdi-arrow-left</v-icon> {{ backLabel }}
            </router-link>
          </nav>

          <div class="blog-post__meta">
            <span v-if="post.category" class="blog-post__badge">{{ post.category.name }}</span>
            <span v-else class="blog-post__badge blog-post__badge--archive">Arquivo</span>
            <time class="blog-post__date" :datetime="post.created_at">{{ formattedDate }}</time>
            <span class="blog-post__author">por {{ post.author?.nickname || "Zinnes" }}</span>
          </div>

          <h1 id="blog-post-title" class="blog-post__title">{{ post.title || "Matéria sem título" }}</h1>

          <img
            v-if="coverUrl"
            :src="coverUrl"
            :alt="post.title ? `Capa da matéria: ${post.title}` : 'Capa da matéria'"
            class="blog-post__cover"
            width="1200"
            height="700"
            fetchpriority="high"
            decoding="async"
          />
        </header>

        <div class="blog-post__body">
          <p v-for="(paragraph, index) in paragraphs" :key="`p-${index}`" class="blog-post__paragraph">
            {{ paragraph }}
          </p>
        </div>

        <div v-if="post.images && post.images.length" class="blog-post__gallery" aria-label="Galeria da matéria">
          <figure v-for="(image, index) in gallery" :key="image.id || index" class="blog-post__gallery-item">
            <button type="button" class="blog-post__gallery-button" :aria-label="`Abrir imagem ${index + 1} da galeria`" @click="openViewer(index)">
              <img :src="image.url" :alt="`Imagem ${index + 1} da matéria`" loading="lazy" decoding="async" width="640" height="400" />
            </button>
          </figure>
        </div>

        <div class="blog-post__actions" role="group" aria-label="Interações com a matéria">
          <button
            type="button"
            class="blog-post__action"
            :class="{ 'blog-post__action--active': post.liked_by_me }"
            :disabled="interacting"
            @click="toggleLike"
          >
            <v-icon small>{{ post.liked_by_me ? "mdi-heart" : "mdi-heart-outline" }}</v-icon>
            <span class="sr-only">Curtir matéria</span>
            <span>{{ post.likes_count || 0 }} {{ post.likes_count === 1 ? "curtida" : "curtidas" }}</span>
          </button>
          <button
            type="button"
            class="blog-post__action"
            :class="{ 'blog-post__action--active': post.saved_by_me }"
            :disabled="interacting"
            @click="toggleSave"
          >
            <v-icon small>{{ post.saved_by_me ? "mdi-bookmark" : "mdi-bookmark-outline" }}</v-icon>
            <span class="sr-only">Salvar matéria</span>
            <span>{{ post.saved_by_me ? "Salvo" : "Salvar" }}</span>
          </button>

          <div v-if="showPostActions" class="blog-post__management" aria-label="Gerenciamento da matéria">
            <button
              v-if="canEditPost"
              type="button"
              class="blog-post__action blog-post__action--edit"
              @click="openEdit"
            >
              <v-icon small>mdi-pencil-outline</v-icon>
              <span>Editar matéria</span>
            </button>
            <button
              v-if="canDeletePost"
              type="button"
              class="blog-post__action blog-post__action--delete"
              @click="confirmDelete = true"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
              <span>Excluir matéria</span>
            </button>
          </div>
        </div>

        <ModalConfirmation v-model="confirmDelete" @confirm="handleDeleteConfirmation" />

        <section class="blog-post__comments" aria-labelledby="blog-comments-title">
          <h2 id="blog-comments-title" class="blog-post__comments-title">Comentários</h2>
          <BlogComments :post-id="post.id" :authenticated="authenticated" @error="notify" />
        </section>
      </article>

      <BlogComposer v-model="composerOpen" :post="editingPost" :submitting="submitting" :error="composerError" @submit="submitPost" />

      <v-dialog v-if="viewerIndex !== null && viewerImages.length" v-model="viewerOpen" max-width="1050" @input="closeViewer">
        <div class="blog-post-viewer">
          <img :src="viewerImages[viewerIndex].url" :alt="`Imagem ${viewerIndex + 1} da galeria em tamanho maior`" class="blog-post-viewer__image" />
          <div class="blog-post-viewer__controls">
            <v-btn icon aria-label="Imagem anterior" :disabled="viewerIndex === 0" @click="stepViewer(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <span>{{ viewerIndex + 1 }} / {{ viewerImages.length }}</span>
            <v-btn icon aria-label="Próxima imagem" :disabled="viewerIndex === viewerImages.length - 1" @click="stepViewer(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
            <v-btn icon aria-label="Fechar visualizador" @click="closeViewer"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </div>
      </v-dialog>

      <v-snackbar v-model="snackbar" color="#31263a" bottom :timeout="5000">
        {{ message }}
        <template #action="{ attrs }">
          <v-btn text color="#f6c945" v-bind="attrs" @click="snackbar = false">Fechar</v-btn>
        </template>
      </v-snackbar>
    </main>
  </div>
</template>

<script>
import BlogComments from "@/components/blog/BlogComments.vue";
import BlogComposer from "@/components/blog/BlogComposer.vue";
import ModalConfirmation from "@/components/ModalConfirmation.vue";
import { socialMediaUrl } from "@/server/SocialService";
import { getBlogPost, likeBlogPost, unlikeBlogPost, saveBlogPost, unsaveBlogPost, updateBlogPost, deleteBlogPost, blogErrorMessage } from "@/server/BlogService";
import { getUser, hasAuth } from "@/server/authStorage";
import { isAdminModeActive } from "@/utils/adminMode";
import "@/styles/blog-page.css";

export default {
  name: "BlogPostView",
  components: { BlogComments, BlogComposer, ModalConfirmation },
  data: () => ({
    loading: true,
    error: false,
    post: null,
    interacting: false,
    composerOpen: false,
    editingPost: null,
    submitting: false,
    composerError: "",
    confirmDelete: false,
    viewerOpen: false,
    viewerIndex: null,
    adminModeActive: false,
    snackbar: false,
    message: "",
  }),
  computed: {
    authenticated() {
      return hasAuth();
    },
    coverUrl() {
      return this.post?.cover_image && this.post.cover_image.url ? socialMediaUrl(this.post.cover_image.url) : "";
    },
    gallery() {
      if (!this.post?.images) return [];
      return this.post.images.filter((image) => !this.post.cover_image || image.id !== this.post.cover_image.id);
    },
    viewerImages() {
      return this.post?.images || [];
    },
    formattedDate() {
      const value = this.post?.created_at;
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "long", year: "numeric" }).format(date);
    },
    paragraphs() {
      const content = this.post?.content || "";
      return content.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== "");
    },
    backLabel() {
      return this.$route.query.origem === "salvos" ? "Voltar aos salvos" : "Voltar ao Portal Zinnes";
    },
    backLink() {
      if (this.$route.query.origem === "salvos") {
        return { name: "BlogSaved" };
      }
      const query = {};
      const category = this.$route.query.categoria;
      const term = this.$route.query.q;
      if (category) query.categoria = category;
      if (term) query.q = term;
      return { name: "BlogHome", query };
    },
    currentUser() {
      return getUser();
    },
    isPostAuthor() {
      return Boolean(
        this.currentUser
        && this.post?.author
        && String(this.currentUser.id) === String(this.post.author.id)
      );
    },
    canManagePost() {
      return this.isPostAuthor || this.adminModeActive;
    },
    canEditPost() {
      return this.canManagePost
        && Boolean(this.post?.can_edit);
    },
    canDeletePost() {
      return this.canManagePost && Boolean(this.post?.can_delete);
    },
    showPostActions() {
      return this.canEditPost || this.canDeletePost;
    },
  },
  watch: {
    "$route.params.slug"() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  mounted() {
    this.updateAdminMode();
    window.addEventListener("adminModeChanged", this.updateAdminMode);
  },
  beforeDestroy() {
    window.removeEventListener("adminModeChanged", this.updateAdminMode);
    document.title = "Zinnes";
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      this.post = null;
      try {
        const body = await getBlogPost(this.$route.params.slug);
        this.post = body.data?.post || null;
        if (!this.post) this.error = true;
        else this.applyMeta();
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    applyMeta() {
      const title = this.post?.title || "Matéria";
      document.title = `${title} | Portal Zinnes`;
      let description = (this.post?.content || "").split(/\r?\n/).map((line) => line.trim()).find((line) => line !== "") || "";
      if (description.length > 160) description = `${description.slice(0, 157).trimEnd()}…`;
      this.ensureMeta("description", description);
    },
    ensureMeta(name, content) {
      let meta = document.head.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    },
    requireAuth() {
      if (this.authenticated) return true;
      this.$router.push({ path: "/login", query: { redirect: this.$route.fullPath } });
      return false;
    },
    async toggleLike() {
      if (!this.requireAuth()) return;
      if (this.interacting) return;
      this.interacting = true;
      const wasLiked = this.post.liked_by_me;
      this.$set(this.post, "liked_by_me", !wasLiked);
      this.$set(this.post, "likes_count", Math.max(0, (this.post.likes_count || 0) + (wasLiked ? -1 : 1)));
      try {
        const body = wasLiked ? await unlikeBlogPost(this.post.id) : await likeBlogPost(this.post.id);
        this.$set(this.post, "liked_by_me", body.data?.liked_by_me ?? !wasLiked);
        this.$set(this.post, "likes_count", body.data?.likes_count ?? this.post.likes_count);
      } catch (error) {
        this.$set(this.post, "liked_by_me", wasLiked);
        this.$set(this.post, "likes_count", (this.post.likes_count || 0) + (wasLiked ? 1 : -1));
        this.notify(blogErrorMessage(error, "Não foi possível curtir a matéria."));
      } finally {
        this.interacting = false;
      }
    },
    async toggleSave() {
      if (!this.requireAuth()) return;
      if (this.interacting) return;
      this.interacting = true;
      const wasSaved = this.post.saved_by_me;
      this.$set(this.post, "saved_by_me", !wasSaved);
      try {
        const body = wasSaved ? await unsaveBlogPost(this.post.id) : await saveBlogPost(this.post.id);
        this.$set(this.post, "saved_by_me", body.data?.saved_by_me ?? !wasSaved);
      } catch (error) {
        this.$set(this.post, "saved_by_me", wasSaved);
        this.notify(blogErrorMessage(error, "Não foi possível salvar a matéria."));
      } finally {
        this.interacting = false;
      }
    },
    openViewer(index) {
      this.viewerIndex = index;
      this.viewerOpen = true;
    },
    stepViewer(step) {
      const next = this.viewerIndex + step;
      if (next >= 0 && next < this.viewerImages.length) this.viewerIndex = next;
    },
    closeViewer() {
      this.viewerOpen = false;
      this.viewerIndex = null;
    },
    async submitPost(payload) {
      this.submitting = true;
      this.composerError = "";
      try {
        const body = await updateBlogPost(this.editingPost.id, {
          title: payload.title,
          content: payload.content,
          categoryId: payload.categoryId,
          images: payload.images,
          keptImageIds: payload.keptImageIds,
        });
        const updated = body.data?.post;
        this.composerOpen = false;
        this.editingPost = null;
        if (updated && updated.slug && updated.slug !== this.$route.params.slug) {
          this.$router.replace({ name: "BlogPost", params: { slug: updated.slug }, query: this.$route.query });
          this.post = updated;
          this.applyMeta();
        } else if (updated) {
          this.post = updated;
          this.applyMeta();
        }
        this.notify("Matéria atualizada.");
      } catch (error) {
        this.composerError = blogErrorMessage(error, "Não foi possível salvar a matéria.");
      } finally {
        this.submitting = false;
      }
    },
    openEdit() {
      if (!this.canEditPost || !this.requireAuth()) return;
      this.editingPost = this.post;
      this.composerError = "";
      this.composerOpen = true;
    },
    async handleDeleteConfirmation(confirmed) {
      this.confirmDelete = false;
      if (!confirmed || !this.post || !this.canDeletePost) return;
      try {
        await deleteBlogPost(this.post.id);
        this.notify("Matéria excluída.");
        this.$router.push({ name: "BlogHome" });
      } catch (error) {
        this.notify(blogErrorMessage(error, "Não foi possível excluir a matéria."));
      }
    },
    notify(message) {
      this.message = message;
      this.snackbar = true;
    },
    updateAdminMode() {
      this.adminModeActive = isAdminModeActive();
    },
  },
};
</script>
